import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import type { QuestionOut, QuickScanFinding } from '@/services/questionsService';
import { getQuestionStatus } from '@/lib/questionUtils';

interface DomainHealthGridProps {
  questions: QuestionOut[];
  quickScanResults: Record<number, QuickScanFinding>;
}

interface DomainStats {
  domain: string;
  total: number;
  ok: number;
  vuln: number;
  clar: number;
  pend: number;
}

function computeDomainStats(
  questions: QuestionOut[],
  quickScanResults: Record<number, QuickScanFinding>,
): DomainStats[] {
  const grouped: Record<string, QuestionOut[]> = {};
  for (const q of questions) {
    const domain = q.security_domain || 'General';
    if (!grouped[domain]) grouped[domain] = [];
    grouped[domain].push(q);
  }

  const stats: DomainStats[] = Object.entries(grouped).map(([domain, qs]) => {
    let ok = 0, vuln = 0, clar = 0, pend = 0;
    for (const q of qs) {
      const s = getQuestionStatus(q, quickScanResults);
      if (s === 'ok') ok++;
      else if (s === 'vulnerable') vuln++;
      else if (s === 'clarification') clar++;
      else pend++;
    }
    return { domain, total: qs.length, ok, vuln, clar, pend };
  });

  // Sort by vulnerabilities descending (worst first)
  stats.sort((a, b) => b.vuln - a.vuln);
  return stats;
}

function healthColor(stats: DomainStats): string {
  if (stats.total === 0 || stats.pend === stats.total) return 'bg-muted text-muted-foreground';
  const vulnRatio = stats.vuln / stats.total;
  if (vulnRatio > 0.5) return 'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-400';
  if (vulnRatio > 0.2 || stats.clar > 0) return 'bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-400';
  return 'bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-400';
}

export default function DomainHealthGrid({ questions, quickScanResults }: DomainHealthGridProps) {
  const stats = computeDomainStats(questions, quickScanResults);

  // Nothing meaningful to show
  if (stats.length === 0 || stats.every((s) => s.pend === s.total)) return null;

  return (
    <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
      {stats.map((s) => (
        <Card key={s.domain}>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between gap-2">
              <CardTitle className="text-sm font-medium truncate">{s.domain}</CardTitle>
              <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold ${healthColor(s)}`}>
                {s.pend === s.total
                  ? 'Pending'
                  : s.vuln / s.total > 0.5
                    ? 'Critical'
                    : s.vuln / s.total > 0.2 || s.clar > 0
                      ? 'Warning'
                      : 'Healthy'}
              </span>
            </div>
          </CardHeader>
          <CardContent className="space-y-2">
            {/* Segmented progress bar */}
            <div className="h-2 w-full rounded-full bg-muted overflow-hidden flex">
              {s.ok > 0 && (
                <div className="bg-green-500 transition-all duration-300" style={{ width: `${(s.ok / s.total) * 100}%` }} />
              )}
              {s.vuln > 0 && (
                <div className="bg-red-500 transition-all duration-300" style={{ width: `${(s.vuln / s.total) * 100}%` }} />
              )}
              {s.clar > 0 && (
                <div className="bg-amber-500 transition-all duration-300" style={{ width: `${(s.clar / s.total) * 100}%` }} />
              )}
            </div>
            {/* Counters */}
            <p className="text-xs text-muted-foreground">
              {s.vuln > 0 && <span className="text-red-600 dark:text-red-400">{s.vuln} vulnerable</span>}
              {s.vuln > 0 && (s.clar > 0 || s.ok > 0) && ' · '}
              {s.clar > 0 && <span className="text-amber-600 dark:text-amber-400">{s.clar} clarification</span>}
              {s.clar > 0 && s.ok > 0 && ' · '}
              {s.ok > 0 && <span className="text-green-600 dark:text-green-400">{s.ok} ok</span>}
              {(s.vuln > 0 || s.clar > 0 || s.ok > 0) && s.pend > 0 && ' · '}
              {s.pend > 0 && <span>{s.pend} pending</span>}
            </p>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
